// The loading screen's ticker: pure helpers (no DOM), so they're unit-testable.
// boot-screen.ts does the drawing.
//
// Three lines, three kinds of REAL data pulled from the snapshot as it unpacks:
//   1. players   "Katie McCabe" · "Arsenal WFC"
//   2. fixtures  "Liverpool WFC 1–3 Arsenal WFC" · "16 May 2026"
//   3. stats     "Pressures per 90" · "9.02"
// Lines 1 + 2 read the current snapshot's field names (nickname/team,
// homeTeamName/…/matchDate) — when the data's shape changes, update those regexes
// (a line with no matches just stays empty). Line 3 is schema-agnostic: any numeric
// `"key": value`, made readable.

/** One ticker line: text on the left, a detail on the right. */
export interface TickerItem {
  left: string;
  right: string;
}

// Stats are only sampled from the first few KB of each chunk — plenty of lines, and it
// keeps the cost of the effect tiny next to the download + decompress it sits on.
export const STATS_SAMPLE_CHARS = 4096;

const PLAYER = /"nickname":\s*"([^"\\]{1,40})"[^]{0,600}?"team":\s*"([^"\\]{1,40})"/g;
const FIXTURE =
  /"matchDate":\s*"(\d{4}-\d{2}-\d{2})",\s*"homeTeamName":\s*"([^"\\]{1,40})",\s*"awayTeamName":\s*"([^"\\]{1,40})",\s*"homeScore":\s*(\d+),\s*"awayScore":\s*(\d+)/g;
const NUMERIC = /"([A-Za-z_]\w{0,40})":\s*(-?\d+(?:\.\d+)?)[,\n}]/g;
// Ids, counters and coordinates aren't interesting to read.
const SKIP_STAT = /(^|_)(id|index|period|second|timestamp|x|y|x_end|y_end)$|Id$|^(x|y)$/;

export function extractPlayers(text: string): TickerItem[] {
  if (!text.includes('"nickname"')) return [];
  return [...text.matchAll(PLAYER)].map(([, name, team]) => ({ left: name!, right: team! }));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-05-16" → "16 May 2026". */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m! - 1]} ${y}`;
}

export function extractFixtures(text: string): TickerItem[] {
  if (!text.includes('"homeTeamName"')) return [];
  return [...text.matchAll(FIXTURE)].map(([, date, home, away, hs, as]) => ({
    left: `${home} ${hs}–${as} ${away}`,
    right: formatDate(date!),
  }));
}

// Words that read better as abbreviations / symbols than title-cased.
const WORDS: Record<string, string> = {
  "90": "per 90",
  np: "NP",
  op: "OP",
  sp: "SP",
  xg: "xG",
  xga: "xGA",
  obv: "OBV",
  padj: "PAdj",
  mpadj: "mPAdj",
  psaa: "PSAA",
  gsaa: "GSAA",
};

/** "np_xg_90" → "NP xG per 90"; "pass_into_pressure_perc" → "Pass into pressure". */
export function humaniseKey(key: string): string {
  const words = key
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split("_")
    .filter((w) => w && w !== "perc") // the % goes on the value (formatStat)
    .map((w) => WORDS[w] ?? w);
  const text = words.join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Up to 2 dp (no trailing zeros). A `perc` stat shows as a percentage (stored as a 0–1 fraction,
 *  or already 0–100). */
export function formatStat(key: string, value: number): string {
  if (/perc/i.test(key)) return `${(value >= 0 && value <= 1 ? value * 100 : value).toFixed(1)}%`;
  return String(Number(value.toFixed(2)));
}

export function extractStats(text: string, max = 20): TickerItem[] {
  const out: TickerItem[] = [];
  for (const [, key, raw] of text.slice(0, STATS_SAMPLE_CHARS).matchAll(NUMERIC)) {
    if (out.length >= max) break;
    if (SKIP_STAT.test(key!)) continue;
    out.push({ left: humaniseKey(key!), right: formatStat(key!, Number(raw)) });
  }
  return out;
}
